import { Component, inject, OnDestroy, OnInit }                from '@angular/core';
import { HttpErrorResponse }                                   from '@angular/common/http';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router }                              from '@angular/router';
import { MatError, MatFormField, MatLabel }                    from '@angular/material/form-field';
import { MatInput }                                            from '@angular/material/input';
import { MatProgressSpinner }                                  from '@angular/material/progress-spinner';
import { mergeMap }                                            from 'rxjs/operators';

import { PasswordCodeNotification } from '../../../../shared/data/password-code-notification';
import { InternalRoutes }           from '../../../../shared/data/internal-routes';
import { MfaOptions }               from '../../../../shared/data/mfa-options';
import { ActionLog }                from '../../../../shared/utils/logging.utils';
import { AuthenticationService }    from '../../../../core/auth/authentication.service';
import { UserAction, UserService }  from '../../../../api/user/user.service';
import { PinNotificationComponent } from '../../../../shared/components/notification/notification.component';
import { ButtonComponent }          from '../../../../shared/components/buttons/button.component';
import { DigitsOnlyDirective }      from '../../../../shared/directives/digits-only.directive';
import { AutoFocusDirective }       from '../../../../shared/directives/auto-focus.directive';

@Component({
  selector: 'pin-enter-verification-code',
  templateUrl: './enter-verification-code.component.html',
  styleUrls: ['../setup-security-token.component.scss'],
  imports: [PinNotificationComponent, ButtonComponent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, DigitsOnlyDirective, AutoFocusDirective, MatError, MatProgressSpinner]
})
export class EnterVerificationCodeComponent implements OnInit, OnDestroy {
  codeParam = 'code';
  mfaOption: string;
  username: string;
  email: string;
  setupPhase: boolean;
  processingRequest: boolean;
  notificationType: PasswordCodeNotification;
  passwordCodeNotificationValues = PasswordCodeNotification;
  authenticatorAppUrl = InternalRoutes.SET_APP_MFA;
  emailUrl = InternalRoutes.SETUP_SECURITY_TOKEN;
  requestingNewCode = false;
  navigatingBack = false;

  private authService = inject(AuthenticationService);
  private userService = inject(UserService);
  private router = inject(Router);
  private formBuilder = inject(UntypedFormBuilder);

  public verifyCodeForm = this.formBuilder.group(
    {code: ['', [Validators.required]]});
  private route = inject(ActivatedRoute);
  private returnUrl: string;
  private resendTimeout: ReturnType<typeof setTimeout>;

  ngOnInit() {
    const queryParams = this.route.snapshot.queryParamMap;
    this.setupPhase = queryParams.has('setup');
    if (queryParams.has('tooManyAttempts')) {
      this.notificationType = this.passwordCodeNotificationValues.too_many_tries;
    }
    this.mfaOption = queryParams.get('mfaOption');
    this.username = queryParams.get('username');
    this.returnUrl = queryParams.has('returnUrl') ? queryParams.get('returnUrl') : '/';
  }

  confirmVerificationCode() {
    if (!this.verifyCodeForm.valid || this.processingRequest) {
      return;
    }
    this.processingRequest = true;
    if (this.setupPhase) {
      if (this.mfaOption === MfaOptions.software_token_mfa) {
        this.confirmMobileAppVerification();
      } else {
        this.confirmSmsOrEmailVerification();
      }
    } else {
      this.sendVerificationCode();
    }
  }

  requestNewCode() {
    if (this.requestingNewCode) {
      return;
    }
    this.requestingNewCode = true;

    const serviceMethod = this.setupPhase === true
      ? () => this.authService.associateEmailToken()
      : () => this.authService.resendEmailMfa();

    serviceMethod()
      .subscribe({
        next: () => {
          this.notificationType = this.passwordCodeNotificationValues.code_resent;
          window.scroll(0, 0);
          this.resendTimeout = setTimeout(() => this.requestingNewCode = false, 2000);
        },
        error: error => {
          this.handleError(error);
          this.resendTimeout = setTimeout(() => this.requestingNewCode = false, 2000);
        }
      });
  }

  handleError(error: HttpErrorResponse, token?: string) {
    switch (error?.error?.message) {
      case 'ERR_CODE_MISMATCH_EXCEPTION':
      case 'ERR_INVALID_PARAMETER':
        this.notificationType = this.passwordCodeNotificationValues.code_incorrect;
        break;
      case 'ERR_EXPIRED_VERIFICATION_CODE':
      case 'ERR_ACCESS_NOT_ALLOWED':
        if (this.setupPhase) {
          this.notificationType = this.passwordCodeNotificationValues.code_expired
        } else {
          this.router.navigate(['/login'], {queryParams: {codeExpired: true, ...this.returnUrlQueryParam()}}).then();
        }
        break;
      case 'ERR_LIMIT_EXCEEDED':
        if (this.setupPhase) {
            this.notificationType = this.passwordCodeNotificationValues.too_many_tries;
        } else {
              this.router.navigate(['/login'], {queryParams: {limitExceededException: true, ...this.returnUrlQueryParam()}}).then();
        }
        if (token) {
          this.blackListOldJwt(token);
        }
        break;
    }
    this.processingRequest = false;
    window.scroll(0, 0);
  }

  backToSetup() {
    if (this.navigatingBack) {
      return;
    }
    this.navigatingBack = true;

    this.authService.getUserSessionToken()
      .subscribe({
        next: token => {
          this.blackListOldJwt(token);
          this.router.navigate([InternalRoutes.SETUP_SECURITY_TOKEN], {queryParams: {returnUrl: this.returnUrl}}).then();
        },
        error: error => {
          this.navigatingBack = false;
          this.handleError(error);
        }
      });
  }

  private sendVerificationCode() {
    this.authService.sendMFACode(this.verifyCodeForm.getRawValue()[this.codeParam], this.username, this.mfaOption)
      .subscribe({
        next: _ => this.continueToHomePage(),
        error: error => this.handleError(error)
      });
  }

  private confirmSmsOrEmailVerification() {
    this.authService.verifyEmailToken(this.verifyCodeForm.getRawValue()[this.codeParam])
      .pipe(
        mergeMap(() => this.authService.getUserSessionToken()))
      .subscribe({
        next: token => this.notifyMfaSetupAndContinue(this.mfaOption === MfaOptions.sms_mfa ? MfaOptions.sms_mfa : MfaOptions.email_mfa, token),
        error: error => this.handleError(error)
      });
  }

  private confirmMobileAppVerification() {
    this.authService.verifySoftwareToken(this.verifyCodeForm.getRawValue()[this.codeParam])
      .pipe(
        mergeMap(() => this.authService.getUserSessionToken()))
      .subscribe({
        next: token => this.notifyMfaSetupAndContinue(MfaOptions.software_token_mfa, token),
        error: error => this.handleError(error)
      });
  }

  private notifyMfaSetupAndContinue(mfaOption: MfaOptions, token: string): void {
    let userAction: UserAction;
    if (this.returnUrl === InternalRoutes.MY_PROFILE) {
      userAction = UserAction.CHANGE_MFA_EMAIL;
    } else {
      userAction = UserAction.SET_MFA_EMAIL;
    }

    this.authService.notifyMfaSetup()
      .pipe(
        mergeMap(_ => this.userService.logUserAction(ActionLog.MFA_SETUP_SUCCESS, mfaOption)),
        mergeMap(_ => this.userService.executeUserAction(userAction)))
      .subscribe({
        next: _ => this.router.navigate([this.returnUrl], {
          queryParams: {mfaOptionType: mfaOption},
          replaceUrl: true
        })
          .then(() => this.blackListOldJwt(token))
          .then(() => this.processingRequest = false),
        error: error => this.handleError(error, token)
      });
  }

  private continueToHomePage() {
    this.router
      .navigate([this.returnUrl])
      .then(() => this.processingRequest = false);
  }

  private returnUrlQueryParam(): { returnUrl?: string } {
    return this.returnUrl && this.returnUrl !== '/' ? {returnUrl: this.returnUrl} : {};
  }

  private blackListOldJwt(token: string) {
    this.userService.revokeJWTBySignature(token).subscribe();
  }

  ngOnDestroy() {
    clearTimeout(this.resendTimeout);
  }
}
